import { GoMakeDialog, GomakePrimaryButton, GomakeTextInput } from "@/components";
import { useStyle } from "./style";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Image from "next/image";
import { useRouter } from "next/router";
import { useGomakeAxios } from "@/hooks";
import { resetPasswordApi } from "@/services/api-service/users/users-api";
import { getCompanyLoginLogo } from "@/services/api-service/profiles/company-profile-api";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import { LoginRecaptcha, resetLoginRecaptcha } from "@/widgets/login/login-recaptcha";
import config from "@/config";
import {
  getResetPasswordRequirements,
  getResetPasswordStrength,
  isResetPasswordPolicyValid,
} from "@/utils/reset-password-policy";

const PASSWORD_REUSE_FALLBACK =
  "You cannot reuse a previously used password. Please choose a new password that you have not used before.";

const getResetPasswordServerMessage = (data: any) => {
  const candidates = [
    data?.message,
    data?.msg,
    data?.item2,
    data?.Item2,
    data?.data?.message,
    data?.data?.msg,
    data?.data?.item2,
    data?.data?.Item2,
    data?.errors?.message,
    data?.errors?.Message,
    data?.errors?.item2,
    data?.errors?.Item2,
    data?.errors?.errorMessage,
    data?.errors?.ErrorMessage,
    data?.data?.error?.message,
    data?.data?.error?.Message,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  if (
    data?.isPasswordReuse === true ||
    data?.IsPasswordReuse === true ||
    data?.errors?.isPasswordReuse === true ||
    data?.errors?.IsPasswordReuse === true ||
    data?.data?.isPasswordReuse === true ||
    data?.data?.IsPasswordReuse === true
  ) {
    return PASSWORD_REUSE_FALLBACK;
  }

  return "";
};

const ResetPasswordLeftSide = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { token } = router.query;
  const { callApi } = useGomakeAxios();
  const recaptchaEnabled = Boolean(config.recaptcha_site_key);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginLogo, setLoginLogo] = useState<string | null>(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState({
    password: false,
    confirmPassword: false,
  });

  const requirements = useMemo(
    () => getResetPasswordRequirements(password),
    [password]
  );
  const strength = useMemo(() => getResetPasswordStrength(password), [password]);
  const policyValid = isResetPasswordPolicyValid(password);
  const passwordsMatch =
    password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;
  const captchaReady = !recaptchaEnabled || recaptchaToken.length > 0;
  const canSubmit =
    Boolean(token) && policyValid && passwordsMatch && captchaReady && !loading;

  const handleCaptchaChange = useCallback((token: string) => {
    setRecaptchaToken(token);
  }, []);

  const handleCaptchaExpire = useCallback(() => {
    setRecaptchaToken("");
  }, []);

  useEffect(() => {
    if (!router.isReady) return;
    if (!token) {
      setErrorMsg(t("resetPassword.invalidToken") || "Invalid reset password token");
      return;
    }

    const fetchLoginLogo = async () => {
      const callback = (res: any) => {
        if (res?.success) {
          const logoUrl = res.data?.data?.loginLogo || res.data?.loginLogo;
          if (logoUrl) {
            setLoginLogo(logoUrl);
          }
        }
      };
      await getCompanyLoginLogo(callApi, callback);
    };

    fetchLoginLogo();
  }, [token, router.isReady, callApi, t]);

  const onClickResetPassword = async () => {
    if (!token) {
      setErrorMsg(t("resetPassword.invalidToken") || "Invalid reset password token");
      return;
    }

    if (!policyValid) {
      setErrorMsg(t("resetPassword.policyViolation"));
      setError({ password: true, confirmPassword: false });
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg(t("login.notMach"));
      setError({ password: true, confirmPassword: true });
      return;
    }

    if (recaptchaEnabled && !recaptchaToken) {
      setErrorMsg(t("login.captchaRequired"));
      return;
    }

    setErrorMsg("");
    setError({ password: false, confirmPassword: false });
    setLoading(true);

    const callback = (data: any) => {
      setLoading(false);

      const crmSuccess =
        data?.item1 === true ||
        data?.Item1 === true ||
        data?.data?.item1 === true ||
        data?.data?.Item1 === true ||
        data?.data?.success === true ||
        (data?.success === true && data?.item1 !== false && data?.Item1 !== false);

      const serverMessage = getResetPasswordServerMessage(data);

      if (crmSuccess) {
        setSuccessMessage(
          serverMessage || t("resetPassword.passwordChangedSuccess")
        );
        setSuccessModalOpen(true);
        return;
      }

      resetLoginRecaptcha();
      setRecaptchaToken("");
      if (serverMessage) {
        setErrorMsg(serverMessage);
      } else {
        setErrorMsg(t("resetPassword.resetFailed") || "Failed to reset password");
      }
    };

    await resetPasswordApi(callApi, callback, {
      token: token as string,
      newPassword: password,
      confirmPassword,
      recaptchaToken: recaptchaEnabled ? recaptchaToken : undefined,
    });
  };

  const goToLogin = () => {
    setSuccessModalOpen(false);
    router.push("/login");
  };

  const { clasess, successIconColor, strengthColor } = useStyle(strength);

  return (
    <div style={clasess.leftContainer}>
      {loginLogo ? (
        <Image
          alt="logo"
          src={loginLogo}
          width={180}
          height={180}
          style={{
            objectFit: "contain",
            borderRadius: 8,
          }}
        />
      ) : (
        <div></div>
      )}
      <div style={clasess.loginContainer}>
        <div style={clasess.loginLbl}>{t("login.resetPassword")}</div>
        <div style={clasess.descriptionLbl}>
          {t("login.setPasswordDescription") || "Please enter your new password below"}
        </div>
        <div style={clasess.inputContainer}>
          <div style={clasess.inputLbl}>{t("login.password")}</div>
          <div style={clasess.input}>
            <GomakeTextInput
              placeholder={t("login.password")}
              onChange={(e) => setPassword(e.target.value)}
              error={error["password"]}
              type="password"
              disabled={loading || !token}
            />
          </div>
        </div>
        <div style={clasess.inputContainer}>
          <div style={clasess.inputLbl}>{t("login.confirmPassword")}</div>
          <div style={clasess.input}>
            <GomakeTextInput
              placeholder={t("login.password")}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={error["confirmPassword"]}
              type="password"
              disabled={loading || !token}
            />
          </div>
        </div>

        {password.length > 0 && (
          <div style={clasess.strengthSection}>
            <div style={clasess.strengthLabel}>
              {t("resetPassword.strengthLabel")}:{" "}
              <span style={{ color: strengthColor }}>
                {t(`resetPassword.strength.${strength}`)}
              </span>
            </div>
            <div style={clasess.strengthBarTrack}>
              <div
                style={{
                  ...clasess.strengthBarFill,
                  width:
                    strength === "weak"
                      ? "33%"
                      : strength === "medium"
                        ? "66%"
                        : "100%",
                  backgroundColor: strengthColor,
                }}
              />
            </div>
          </div>
        )}

        <ul style={clasess.requirementsList}>
          {requirements.map((req) => (
            <li key={req.key} style={clasess.requirementItem}>
              {req.met ? (
                <CheckCircleOutlineRounded
                  sx={{ fontSize: 18, color: strengthColor }}
                />
              ) : (
                <RadioButtonUncheckedRounded
                  sx={{ fontSize: 18, color: clasess.requirementIconMuted }}
                />
              )}
              <span
                style={
                  req.met
                    ? clasess.requirementMet
                    : clasess.requirementUnmet
                }
              >
                {t(req.labelKey)}
              </span>
            </li>
          ))}
        </ul>

        {recaptchaEnabled && (
          <div style={clasess.recaptchaContainer}>
            <LoginRecaptcha
              onChange={handleCaptchaChange}
              onExpire={handleCaptchaExpire}
            />
          </div>
        )}

        <div style={clasess.errorMsgStyle}>{errorMsg}</div>
        <div style={clasess.btnContainer}>
          <GomakePrimaryButton
            onClick={onClickResetPassword}
            disabled={!canSubmit}
          >
            {loading
              ? t("resetPassword.resetting") || "Resetting..."
              : t("login.resetPassword")}
          </GomakePrimaryButton>
        </div>
      </div>

      <GoMakeDialog
        open={successModalOpen}
        onClose={goToLogin}
        title={t("resetPassword.successTitle")}
        hideCancelButton
        primaryButtonLabel={t("login.goToLogin")}
        onPrimaryAction={goToLogin}
        maxWidth="sm"
        fullWidth
      >
        <div style={clasess.successDialogBody} role="status" aria-live="polite">
          <div style={clasess.successIconCircle} aria-hidden>
            <CheckCircleRounded
              sx={{
                fontSize: 52,
                color: successIconColor,
              }}
            />
          </div>
          <div style={clasess.successMessage}>{successMessage}</div>
        </div>
      </GoMakeDialog>
    </div>
  );
};
export { ResetPasswordLeftSide };
